import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { levelLabel } from "@/lib/levels";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Play, Download, Mail, Clock, ChevronDown, Gamepad2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AgendaView() {
  const { classId } = useParams();
  const [cls, setCls] = useState(null);
  const [openBlocks, setOpenBlocks] = useState({});
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/classes/${classId}`).then(({ data }) => setCls(data)).catch(() => toast.error("Could not load agenda"));
  }, [classId]);

  const toggle = (i) => setOpenBlocks((o) => ({ ...o, [i]: !o[i] }));

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/classes/${classId}/agenda.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `agenda_${(cls?.title || "class").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const sendEmail = async () => {
    if (!email.trim()) { toast.error("Enter an email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/classes/${classId}/agenda/email`, { email: email.trim() });
      toast.success(`Agenda emailed to ${data.sent_to}`);
      setEmailOpen(false);
      setEmail("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!cls) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#545454]">Loading…</div>;

  const total = cls.blocks.reduce((sum, b) => sum + (parseInt(b.duration_min, 10) || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="agenda-view">
      <Link to="/director/agenda" className="inline-flex items-center gap-1 text-sm font-semibold text-[#920e12] mb-6">
        <ArrowLeft size={16} /> Back to library
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-extrabold text-[#bf6163]">
            {levelLabel(cls.level)} · Session {cls.session_no} · Class {cls.class_no}
          </div>
          <h1 className="mt-1 font-display text-4xl text-[#550001]">{cls.title || "Class Agenda"}</h1>
          <p className="mt-1 text-[#545454]">{cls.blocks.length} segments · {total} min total run time</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="agenda-run-btn">
            <Link to={`/director/run/${classId}`}><Play size={16} className="mr-1" /> Run live</Link>
          </Button>
          <Button onClick={downloadPdf} variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="agenda-download-btn">
            <Download size={16} className="mr-1" /> Download PDF
          </Button>
          <Button onClick={() => setEmailOpen(true)} variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="agenda-email-btn">
            <Mail size={16} className="mr-1" /> Email
          </Button>
        </div>
      </div>

      <ol className="mt-8 space-y-3" data-testid="agenda-blocks">
        {cls.blocks.map((b, i) => {
          const isOpen = openBlocks[i];
          const hasDetail = b.content || b.game;
          return (
            <li key={i} className="bg-white rounded-2xl border border-[#dfd8da] overflow-hidden" data-testid={`agenda-block-${i}`}>
              <button
                onClick={() => hasDetail && toggle(i)}
                data-testid={`agenda-block-toggle-${i}`}
                className={`w-full flex items-center gap-4 p-4 text-left ${hasDetail ? "cursor-pointer hover:bg-[#f9f6f6]" : "cursor-default"}`}
                aria-expanded={isOpen}
              >
                <span className="w-8 h-8 shrink-0 rounded-full bg-[#920e12] text-white text-sm flex items-center justify-center font-bold">{i + 1}</span>
                <span className="shrink-0 flex items-center gap-1 text-sm text-[#545454] w-20"><Clock size={14} /> {b.duration_min} min</span>
                <span className="shrink-0 text-xs uppercase tracking-wide font-extrabold text-[#bf6163] w-32">{b.type}</span>
                <span className="flex-1 font-bold text-[#1a1617]">{b.title || (b.game?.name) || "—"}</span>
                {hasDetail && <ChevronDown size={18} className={`shrink-0 text-[#920e12] transition-transform ${isOpen ? "rotate-180" : ""}`} />}
              </button>
              {isOpen && hasDetail && (
                <div className="px-4 pb-4 pl-16 space-y-2" data-testid={`agenda-block-detail-${i}`}>
                  {b.game && (
                    <div className="flex items-start gap-2 text-sm">
                      <Gamepad2 size={16} className="text-[#920e12] mt-0.5 shrink-0" />
                      <div>
                        <Link to={`/games/${b.game.id}`} className="font-bold text-[#920e12] underline">{b.game.name}</Link>
                        {b.game.description && <p className="text-[#545454] mt-0.5">{b.game.description}</p>}
                      </div>
                    </div>
                  )}
                  {b.content && <p className="text-sm text-[#1a1617] leading-relaxed">{b.content}</p>}
                </div>
              )}
            </li>
          );
        })}
        {cls.blocks.length === 0 && <p className="text-[#545454]">This class has no blocks yet.</p>}
      </ol>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="agenda-email-dialog">
          <DialogHeader>
            <DialogTitle>Email this agenda</DialogTitle>
            <DialogDescription>
              Send the full agenda to any registered program account (director, participant, or caregiver).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="agenda-email">Recipient email</Label>
            <input
              id="agenda-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              data-testid="agenda-email-input"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} className="bg-[#920e12] text-white" data-testid="agenda-email-send">
              {sending ? <><Loader2 size={16} className="mr-1 animate-spin" /> Sending…</> : <><Mail size={16} className="mr-1" /> Send agenda</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
